import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "Share on X",
    description: "Share your job posting on X (Twitter) to reach a global audience",
    share_button: "Share on X",
    back: "Back to channels",
    default_text: "We're hiring!",
    loading: "Loading...",
  },
  fr: {
    title: "Partager sur X",
    description: "Partagez votre offre d'emploi sur X (Twitter) pour atteindre un public mondial",
    share_button: "Partager sur X",
    back: "Retour aux canaux",
    default_text: "Nous recrutons !",
    loading: "Chargement...",
  },
};

export default function XShareChannel({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelSlug = "share-on-x";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const channelResponse = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (channelResponse.ok && channelResponse.data?.[0]) {
        setChannel(channelResponse.data[0]);
      }
    } catch (e) {
      console.error("Error loading channel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const handleShare = () => {
    if (!shareLink) return;
    const text = `${t("default_text")} ${job?.title || ""} - ${shareLink}`;
    const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterShareUrl, "_blank", "width=600,height=600");
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-600">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          {channel?.logo ? (
            <img src={channel.logo} alt={channel.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center shrink-0">
              <FaXTwitter className="w-8 h-8 text-white" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{channel?.description || t("description")}</p>

            {jobPost && (
              <div className="flex items-center gap-2">
                <button onClick={handleShare} className="btn btn-primary flex items-center gap-2 bg-gray-900 hover:bg-black">
                  <FaXTwitter className="w-4 h-4" />
                  {t("share_button")}
                  <FaExternalLinkAlt className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
